<template>
  <v-container fluid grid-list>
    <v-layout>
      <v-flex md10>
        <v-card>
          <v-form v-if="project" v-model="validForm" ref="createItemForm" lazy-validation>
            <v-container fluid>
              <v-layout v-bind="layout">
                <v-flex md3>
                  <upload :project="project" />
                </v-flex>

                <v-flex md9 px-4>
                  <steps :project="project"
                         :shipping-address="shippingAddress"
                         :shipping-method="shippingMethod"
                         @save-shipping-address="saveShippingAddress($event)"
                         @save-shipping-method="saveShippingMethod($event)"
                         @save-project="saveItem($event)" />
                </v-flex>
             </v-layout>
            </v-container>
          </v-form>
        </v-card>
      </v-flex>
      <v-flex lg2>
        <v-card class="sticky">
          <item-summary :project="project"
                        :valid="valid"
                        :has-files="hasFiles" />
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
  import api from '@/api/gungho'
  import Events from '@/api/events'
  import Summary from './summary'
  import Upload from './upload'
  import Steps from './steps'

  export default {
    components: {
      itemSummary: Summary,
      upload: Upload,
      steps: Steps
    },

    data () {
      return {
        validForm: false,
        project: {
          product: {},
          unit_prices: [],
          file: {}
        },
        productNames: ['Basic Business Plan', 'PMP Business Plan'],
        shippingAddress: {},
        shippingMethod: '',
        quantity: 1,
        fileProgress: 0,
        showProgress: false,
        processing: false,
        requiredRules: [
          v => !!v || 'Cannot be blank'
        ]
      }
    },

    computed: {
      layout () {
        const layout = {}
        if (this.$vuetify.breakpoint.mdAndDown) layout.column = true
        return layout
      },

      hasFiles () {
        return !!(this.project.file && this.project.file_id)
      },

      valid () {
        return !!(this.validForm && this.hasFiles)
      },

      width () {
        if (!this.project || !this.project.product || !this.project.product.properties) return 8.5
        var obj = this.project.product.properties.find(function (obj) { return obj.name === 'width' })
        return obj.default
      },

      length () {
        if (!this.project || !this.project.product || !this.project.product.properties) return 11
        var obj = this.project.product.properties.find(function (obj) { return obj.name === 'length' })
        return obj.default
      }
    },

    watch: {
      'quantity': function () {
        if (parseInt(this.quantity) >= 500) {
          this.quantity = 1
          Events.$emit('error', 'Please Contact Gung-Ho for pricing on 500+ quantities')
        }
      }
    },

    methods: {
      updateUnitPrice () {
        if (this.project.file.original_pages) {
          this.project.file.page_count = this.project.file.content_pages
          api.unitPrices(this.project.file).then(data => {
            this.project.unit_price = data.unit_price
            this.project.unit_prices = data.unit_prices
          }).catch(error => {
            console.log(error)
          })
        }
      },

      updateCart (id, quantity) {
        if (!this.isValid()) return false
        this.$store.dispatch('addCartItem', { project_id: id, quantity: quantity })
      },

      nextPrice () {
        if (this.quantity < 5) {
          return this.$options.filters.currency(this.project.unit_prices[5]) + ' for 5–99 Qty'
        } else if (this.quantity < 100) {
          return this.$options.filters.currency(this.project.unit_prices[100]) + ' for 100-249 Qty'
        } else if (this.quantity < 250) {
          return this.$options.filters.currency(this.project.unit_prices[250]) + ' for 250-499 Qty'
        } else {
          return ''
        }
      },

      unitPrice () {
        if (this.quantity >= 250 && this.quantity < 500) return this.project.unit_prices[250]
        else if (this.quantity >= 100 && this.quantity < 250) return this.project.unit_prices[100]
        else if (this.quantity >= 5 && this.quantity < 100) return this.project.unit_prices[5]
        if (this.quantity < 5) return this.project.unit_prices[1]
      },

      changeQuantity (by) {
        this.quantity = parseInt(this.quantity)
        if ((this.quantity + by) > -1 && (this.quantity + by) < 500) {
          this.quantity += by
        }
      },

      saveShippingAddress (address) {
        console.log('saveShippingAddress: ', address)
        this.shippingAddress = address
        api.saveCartAddress(this.shippingAddress)
      },

      saveShippingMethod (method) {
        console.log('saveShippingMethod: ', method)
        this.shippingMethod = method
      },

      saveItem () {
        if (!this.isValid()) return false
        Events.$emit('toast', 'Saving Item')
        if (this.project.id) this.updateItem()
        else this.createItem()
      },

      createItem () {
        api.saveProject(this.project).then(data => {
          Events.$emit('toast', 'Item Saved')
          this.project = data.project
        }).catch(data => {
          console.log(data)
        })
      },

      updateItem () {
        api.updateProject(this.project).then(data => {
          Events.$emit('toast', 'Item Updated')
          this.project = data.project
        }).catch(data => {
          console.log(data)
        })
      },

      isNotSet (v) {
        return v === undefined || v === null
      },

      pageLimit () {
        if (this.project.pages < 20) {
          Events.$emit('toast', 'Perfect Binding requires a minimum of 10 sheets (20 pages)')
        }
      },

      isValid () {
        this.$refs.createItemForm.validate()
        if (!this.hasFiles) {
          Events.$emit('error', 'PDF file required')
          return false
        } else if (!this.valid) {
          Events.$emit('error', 'Missing required fields')
          return false
        } else if (this.project.file.content_pages < 20 && this.project.file.binding === 'perfect') {
          Events.$emit('error', 'Perfect Binding requires a minimum of 10 sheets (20 pages)')
          return false
        } else {
          return true
        }
      },

      setDefaults () {
        for (var i in this.project.product.properties) {
          var property = this.project.product.properties[i]
          if (!property.pinned && this.isNotSet(this.project.file[property.name])) {
            this.$set(this.project.file, property.name, property.default)
          }
        }
      },

      newItem () {
        api.product(this.productNames[0]).then(data => {
          this.project = {
            name: '',
            unit_prices: {},
            product_id: data.product.id,
            product: data.product,
            file_id: null,
            file_type: data.product.kind,
            file: {
              product_id: data.product.id,
              content_pages: 0,
              original_pages: 0,
              files: {},
              thumbs: {},
              unit_prices: {}
            }
          }
          this.setDefaults()
        }).catch(error => {
          console.log(error)
        })
      }
    },

    beforeMount () {
      this.newItem()
      this.$store.dispatch('loadStates')
      this.$store.dispatch('loadCountries')
      this.$store.dispatch('loadProducts')
    }
  }
</script>



// WEBPACK FOOTER //
// item.vue?c9178d9c